import { useState, useEffect } from 'react';
import { getImageSrc, coverOf } from '../../utils/media';

/**
 * The square media area of a product card: the cover image, or a branded tile
 * with the product's initial when there is no image to show.
 *
 * Both fill exactly the same square so a grid of cards stays aligned whether or
 * not every product has a picture.
 */
export default function ProductThumb({ product, className = '', rounded = 'rounded-t-2xl', dimmed = false }) {
  const cover = coverOf(product);
  const [failed, setFailed] = useState(false);

  // A product can be swapped in place (search, category filter), and a stale
  // failure would hide a perfectly good image.
  useEffect(() => setFailed(false), [cover?.mediaLink]);

  const showImage = cover && !failed;

  return (
    <div className={`relative aspect-square overflow-hidden bg-havana-cream ${rounded} ${className}`}>
      {showImage ? (
        <img
          src={getImageSrc(cover.mediaLink)}
          alt={product.name}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${
            dimmed ? 'opacity-40 grayscale' : ''
          }`}
        />
      ) : (
        <div
          className={`w-full h-full bg-gradient-to-br from-primary-500 to-primary-400 flex items-center justify-center ${
            dimmed ? 'opacity-40' : ''
          }`}
        >
          <span className="text-3xl font-bold text-white/90">{product.name?.charAt(0)}</span>
        </div>
      )}

      {/* One dot per image, so a card hints at the rest of the gallery */}
      {product.images?.length > 1 && (
        <div className="absolute bottom-2 right-2 flex gap-1">
          {product.images.slice(0, 4).map((image, idx) => (
            <span
              key={image.id}
              className={`w-1.5 h-1.5 rounded-full shadow-sm ${idx === 0 ? 'bg-white' : 'bg-white/60'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
